/**
 * Environment constants
 * These are loaded at build time from environment variables
 * 
 * How it works:
 * - For all platforms (web, iOS, Android): Values come from Constants.expoConfig.extra
 *   The .env file is loaded by app.config.js via dotenv.config() and put in expo.extra
 *   Expo automatically exposes expo.extra via Constants.expoConfig.extra for all platforms
 * - For web production builds: Falls back to process.env (exposed via webpack DefinePlugin)
 */

import Constants from 'expo-constants';
import { Platform } from 'react-native';

// Helper to get value from Expo Constants
// Works for both web and native platforms
const getEnvValue = (key: string, fallback?: string): string => {
  // Get value from Constants.expoConfig.extra (works for all platforms)
  if (Constants.expoConfig?.extra && key in Constants.expoConfig.extra) {
    const value = Constants.expoConfig.extra[key];
    if (value && typeof value === 'string' && value !== '') {
      return value;
    }
  }
  
  // Fallback to process.env for web production builds
  // Webpack DefinePlugin exposes environment variables via process.env
  if (Platform.OS === 'web' && typeof process !== 'undefined' && process.env) {
    const envValue = process.env[key];
    if (envValue && typeof envValue === 'string' && envValue !== '') {
      return envValue;
    }
  }
  
  // Use fallback if provided
  if (fallback) {
    return fallback;
  }
  
  // Throw error if value is not found
  throw new Error(`Environment variable ${key} is not defined. Make sure it's in your .env file and app.config.js`);
};

// Supabase configuration
// Values come from Constants.expoConfig.extra (populated by app.config.js)
export const SUPABASE_URL = getEnvValue('VITE_SUPABASE_URL');
export const SUPABASE_ANON_KEY = getEnvValue('VITE_SUPABASE_ANON_KEY');

// Stripe configuration
export const STRIPE_ENV = getEnvValue('VITE_STRIPE_ENV', 'test');
export const STRIPE_PUBLISHABLE_KEY = getEnvValue('VITE_STRIPE_PUBLISHABLE_KEY');
export const STRIPE_PUBLISHABLE_KEY_TEST = getEnvValue('VITE_STRIPE_PUBLISHABLE_KEY_TEST');

// OpenAI configuration
export const OPENAI_API_KEY = getEnvValue('OPENAI_API_KEY');

// Google Places API configuration
export const GOOGLE_PLACES_API_KEY = getEnvValue('GOOGLE_PLACES_API_KEY');

// Email notifications toggle (set DISABLE_EMAIL_NOTIFICATIONS=true to disable)
export const DISABLE_EMAIL_NOTIFICATIONS = getEnvValue('DISABLE_EMAIL_NOTIFICATIONS', 'false') === 'true';

// Environment mode
export const ENV_MODE = Platform.OS === 'web' ? 'development' : 'production';

// Verification: Log what values we actually got (for debugging)
console.log('[constants.ts] ==========================================');
console.log('[constants.ts] Verification - Values loaded:');
console.log('[constants.ts]   Platform.OS:', Platform.OS);
console.log('[constants.ts]   Constants.expoConfig?.extra:', Constants.expoConfig?.extra);
console.log('[constants.ts]   SUPABASE_URL:', SUPABASE_URL ? `${SUPABASE_URL.substring(0, 30)}...` : 'UNDEFINED');
console.log('[constants.ts]   SUPABASE_ANON_KEY:', SUPABASE_ANON_KEY ? 'LOADED' : 'UNDEFINED');
console.log('[constants.ts] ==========================================');

